import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { FunctionComponent, useState, useCallback, useEffect, useRef } from 'react';

import ViewMoreButton from '@@src/components/Buttons/ViewMoreButton';
import { hash } from '@@utils/helpers';

export interface ViewMoreTextProps {
  inputText: string;
  lineClamp?: number;
}

const useStyles = makeStyles<Theme, Partial<ViewMoreTextProps>>((theme: Theme) => {
  return createStyles({
    root: {
      alignItems: 'end',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
      },
    },
    text: {
      display: 'block',
      paddingBottom: 0,
      marginBottom: 0,
      '&.collapsed': {
        display: '-webkit-box',
        '-webkit-line-clamp': ({ lineClamp = 4 }) => {
          return lineClamp;
        },
        '-webkit-box-orient': 'vertical',
        overflow: 'hidden',
      },
    },
    viewMoreButtonContainer: {
      display: 'inline',
    },
    viewMoreButton: {
      marginTop: 16,
    },
  });
});

const ViewMoreText: FunctionComponent<ViewMoreTextProps> = ({ inputText, ...props }) => {
  const classes = useStyles(props);
  const [showViewMore, setShowViewMore] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const paragraphRef = useRef<HTMLParagraphElement>(null);
  const id = `biography-${hash(inputText)}`;

  const handleExpandEvent = useCallback(() => {
    setIsExpanded(true);
  }, [setIsExpanded]);

  const handleCollapseEvent = useCallback(() => {
    setIsExpanded(false);
    setTimeout(() => {
      paragraphRef.current.scrollIntoView({ block: 'end', inline: 'nearest', behavior: 'smooth' });
    }, 50);
  }, [setIsExpanded]);

  useEffect(() => {
    const isTextClamped = (elm: HTMLParagraphElement) => {
      return elm.scrollHeight > elm.clientHeight;
    };

    new ResizeObserver((e) => {
      setShowViewMore(isTextClamped(e[0].target));
    }).observe(paragraphRef.current);
  }, []);

  return (
    <div className={classes.root}>
      <p ref={paragraphRef} id={id} className={clsx(classes.text, { collapsed: !isExpanded })}>
        {inputText}
      </p>
      {(showViewMore || isExpanded) && (
        <div className={classes.viewMoreButtonContainer}>
          <ViewMoreButton
            onExpand={handleExpandEvent}
            onCollapse={handleCollapseEvent}
            classes={{ viewMoreButton: classes.viewMoreButton }}
            chevronPosition="after"
            chevronDirection="vertical"
            aria-controls={id}
            aria-expanded={isExpanded}
          />
        </div>
      )}
    </div>
  );
};

export default ViewMoreText;
